import { Stack } from '@mui/material';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { OrchestratorStatusChip } from '@/console/features/orchestrators/components/orchestrator-status-chip/orchestrator-status-chip';
import { VplcOptionInfo } from '@/console/features/orchestrators/components/vplc-picker/vplc-option-info';
import { TVplcPickerFormValues } from '@/console/features/orchestrators/components/vplc-picker/vplc-picker.types';
import { PROVISIONING_I18N_PREFIX } from '@/console/features/orchestrators/orchestrators.constants';
import { isOrchestratorActive } from '@/console/features/orchestrators/utils/orchestrator-status';
import { getVplcBinding } from '@/console/features/orchestrators/utils/vplc-binding';
import { Alert } from '@/shared/components/ui/alert/alert';
import { FormSelect } from '@/shared/components/ui/form/components/form-select/form-select';
import {
  useReadOrchestratorsQuery,
  useReadVplcsQuery,
} from '@/shared/redux/api/openplc/openplc-api';

const BINDING = `${PROVISIONING_I18N_PREFIX}.binding`;

export const VplcPicker: React.FC = () => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<TVplcPickerFormValues>();
  const orchestratorId = useWatch({ control, name: 'softPlc.orchestratorId' });
  const { data: orchestratorsData, isLoading: isLoadingOrchestrators } =
    useReadOrchestratorsQuery();
  const {
    data: vplcsData,
    isFetching: isFetchingVplcs,
    isError: isVplcsError,
  } = useReadVplcsQuery(
    { orchestratorId: orchestratorId ?? '' },
    { skip: !orchestratorId },
  );

  const orchestrators = orchestratorsData?.orchestrators ?? [];
  const vplcs = orchestratorId ? (vplcsData?.vplcs ?? []) : [];

  const handleOrchestratorChange = () => {
    setValue('softPlc.autonomyVplcId', '', { shouldValidate: true });
    setValue('softPlc.ipAddress', '');
  };

  const handleVplcChange = (autonomyId?: unknown) => {
    const vplc = vplcs.find((item) => item.autonomyId === autonomyId);

    setValue('softPlc.ipAddress', vplc?.ipAddress ?? '');
  };

  const getVplcsNotice = () => {
    if (!orchestratorId || isFetchingVplcs) {
      return;
    }

    if (isVplcsError) {
      return t(`${PROVISIONING_I18N_PREFIX}.vplcs.list.load_error`);
    }

    if (!vplcs.length) {
      return t(`${PROVISIONING_I18N_PREFIX}.vplcs.list.empty`);
    }
  };

  const vplcsNotice = getVplcsNotice();

  return (
    <Stack gap="1rem">
      <Alert color="blue" text={t(`${BINDING}.hint`)} />
      <FormSelect<TVplcPickerFormValues>
        name="softPlc.orchestratorId"
        label={t(`${BINDING}.orchestrator`)}
        required
        disabled={isLoadingOrchestrators}
        onSelectChange={handleOrchestratorChange}
        options={orchestrators.map((orchestrator) => ({
          value: orchestrator.orchestratorId,
          label: orchestrator.name,
          additionalInfo: (
            <OrchestratorStatusChip orchestrator={orchestrator} />
          ),
          // An unreachable orchestrator cannot list its vPLCs.
          disabled: !isOrchestratorActive(orchestrator.status),
        }))}
      />
      <FormSelect<TVplcPickerFormValues>
        name="softPlc.autonomyVplcId"
        label={t(`${BINDING}.vplc`)}
        required
        disabled={!orchestratorId || isFetchingVplcs}
        onSelectChange={handleVplcChange}
        options={vplcs.flatMap((vplc) =>
          vplc.autonomyId
            ? [
                {
                  value: vplc.autonomyId,
                  label: vplc.name,
                  additionalInfo: <VplcOptionInfo vplc={vplc} />,
                  disabled: !getVplcBinding(vplc).isBindable,
                },
              ]
            : [],
        )}
      />
      {vplcsNotice && <Alert color="yellow" text={vplcsNotice} />}
    </Stack>
  );
};
